import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Header } from '../../../components/common/Header';
import { useAttachmentsApi } from '../../../app/api/useAttachmentsApi';
import { useSnackbar } from '../../../navigation/snackbar';
import { AttachmentMetadata } from '@codey2/codey-api-types/models';
import styles from '../ExercisePage.module.css';
import attachmentStyles from './AssetsTab.module.css';
import { DeleteIcon } from '../../../components/common/icons/deleteIcon';

interface AssetsTabProps {
  exerciseId?: number;
}

export const AssetsTab: React.FC<AssetsTabProps> = ({ exerciseId }) => {
  const [attachments, setAttachments] = useState<AttachmentMetadata[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { getAttachments, uploadAttachment, deleteAttachment, downloadAttachment } = useAttachmentsApi();
  const { showSnackbar } = useSnackbar();

  const loadAttachments = useCallback(async () => {
    if (!exerciseId) return;

    try {
      const data = await getAttachments(exerciseId);
      setAttachments(data);
    } catch {
      showSnackbar('Kon bestanden niet laden. Probeer het later opnieuw.', 'error');
    }
  }, [exerciseId, getAttachments, showSnackbar]);

  useEffect(() => {
    loadAttachments();
  }, [exerciseId, loadAttachments]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!exerciseId) return;

    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const uploaded = await uploadAttachment(exerciseId, file);
      setAttachments(prev => [...prev, uploaded]);
      showSnackbar(`Bestand "${file.name}" succesvol geüpload`, 'success');

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch {
      showSnackbar('Het uploaden van het bestand is mislukt. Probeer het later opnieuw.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (attachmentId: number, filename: string) => {
    if (!exerciseId) return;

    setDeletingIds(prev => new Set(prev).add(attachmentId));
    try {
      await deleteAttachment(exerciseId, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      showSnackbar(`Bestand "${filename}" verwijderd`, 'success');
    } catch {
      showSnackbar('Het verwijderen van het bestand is mislukt. Probeer het later opnieuw.', 'error');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachmentId);
        return newSet;
      });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Show message if no exercise ID is provided (creating new exercise)
  if (!exerciseId) {
    return (
      <div>
        <Header size={5} content="Bestanden" className={styles.stepsHeader} />
        <p className={attachmentStyles.infoMessage}>Je dient de opdracht op te slaan voordat je bestanden kunt toevoegen.</p>
      </div>
    );
  }

  return (
    <div>
      <Header size={5} content="Bestanden" className={styles.stepsHeader} />
      <p>Beheer hier de bestanden die bij de opdracht horen.</p>

      {/* Upload Section */}
      <div className={attachmentStyles.uploadSection}>
        <input
          ref={fileInputRef}
          type="file"
          id="file-upload"
          className={attachmentStyles.fileInput}
          onChange={handleFileSelect}
          disabled={isUploading}
        />
        <label htmlFor="file-upload" className={attachmentStyles.uploadButton}>
          {isUploading ? (
            <>
              <span className={attachmentStyles.spinner}></span>
              Uploaden...
            </>
          ) : (
            <>📎 Bestand toevoegen</>
          )}
        </label>
      </div>

      {/* Attachments List */}
      {attachments.length === 0 ? (
        <p className={attachmentStyles.emptyState}>Nog geen bestanden toegevoegd</p>
      ) : (
        <div className={attachmentStyles.attachmentsList}>
          {attachments.map(attachment => (
            <div key={attachment.id} className={attachmentStyles.attachmentItem}>
              <div className={attachmentStyles.attachmentInfo}>
                <a href={downloadAttachment(exerciseId, attachment.id)} download={attachment.filename} className={attachmentStyles.attachmentName}>
                  📄 {attachment.filename}
                </a>
                <div className={attachmentStyles.attachmentMeta}>
                  <span>{attachment.contentType}</span>
                  <span>{formatFileSize(attachment.size)}</span>
                  <span>{formatDate(attachment.uploadedAt)}</span>
                </div>
              </div>
              <button
                className={attachmentStyles.deleteButton}
                onClick={() => handleDelete(attachment.id, attachment.filename)}
                disabled={deletingIds.has(attachment.id)}
                title="Verwijderen"
              >
                {deletingIds.has(attachment.id) ? <span className={attachmentStyles.spinner}></span> : <DeleteIcon />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
